import React, { useContext, useEffect, useState } from 'react';
import { LoyaltyMember } from '@teko/loyalty';
import { useAuth } from '@teko/auth-react';
import { logger } from '@teko/log';
import { useTenantConfig } from './GlobalDataContext';
import { useServices } from './ServiceContext';

interface LoyaltyProviderInterface {
  children: React.ReactNode;
}

interface LoyaltyContextInterface {
  loyaltyMemberInfo?: LoyaltyMember;
  loading: boolean;
  setTriggerGetMemberInfo: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoyaltyContext = React.createContext<LoyaltyContextInterface | undefined>(
  undefined,
);

export const useLoyalty = () => {
  const context = useContext(LoyaltyContext);
  if (context === undefined) {
    throw new Error('useLoyalty must be used within a LoyaltyProvider');
  }
  return context;
};

export const LoyaltyProvider: React.FC<LoyaltyProviderInterface> = ({
  children,
}) => {
  const { needToCheckLoyaltyMemberId, notFetchLoyaltyMemberInfoGlobally } =
    useTenantConfig();
  const [loyaltyMemberInfo, setLoyaltyMemberInfo] = useState<LoyaltyMember>();
  const [triggerGetMemberInfo, setTriggerGetMemberInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { loyaltyBFFService } = useServices();

  useEffect(() => {
    // call getLoyaltyInfo only when user account had phone number
    const shouldFetchLoyaltyInfo = !!needToCheckLoyaltyMemberId
      ? user?.telephone && user?.loyaltyMemberId
      : user?.telephone;
    if (shouldFetchLoyaltyInfo) {
      const getLoyaltyInfo = async () => {
        try {
          setLoading(true);
          const loyaltyClientId = loyaltyBFFService.getClientId();
          if (loyaltyClientId) {
            if (!notFetchLoyaltyMemberInfoGlobally) {
              const data = await loyaltyBFFService.getLoyaltyMemberInfo();
              setLoyaltyMemberInfo(data);
            }
          }
        } catch (e) {
          logger.error(e);
        } finally {
          setLoading(false);
        }
      };
      getLoyaltyInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.telephone, triggerGetMemberInfo]);

  return (
    <LoyaltyContext.Provider
      value={{
        loyaltyMemberInfo,
        loading,
        setTriggerGetMemberInfo,
      }}
    >
      {children}
    </LoyaltyContext.Provider>
  );
};
